import { DELIVERY_CUSTOMIZATION_ACTIVE } from 'constants/integrationMode';
import useCheckoutMapSettings from 'features/settings/components/ppoints/design-settings/CheckoutMapSettings/hooks/useCheckoutMapSettings';
import useCheckoutMapSettingsSWR from 'features/settings/components/ppoints/design-settings/CheckoutMapSettings/hooks/useCheckoutMapSettingsSWR';
import useErrorBanner from 'hooks/error-banner/useErrorBanner';
import { useCheckoutMapSettingsMapper } from 'hooks/useCheckoutMapSettingsMapper';
import { IntegrationModeSetting } from 'models/ppoints/settings/SettingsToEcom';
import { useCallback, useState } from 'react';
import useSetIntegrationMode from './useSetIntegrationMode';

export interface ActivateIntegrationModeOptions {
    /** Saved with the thank-you page map integration */
    googleMapApiKey?: string;
}

/**
 * Activates an integration mode from the wizard — its integration step and the default-mode fallback. The switch
 * itself (carrier service, the mode, the Smart Delivery Page metafield, the delivery customization and their error
 * banners) is `useSetIntegrationMode`'s; this adds what only the wizard needs: saving the Google Maps API key with the
 * thank-you page map, the inline carrier service warning, and Shopify's loading bar. The wizard step itself is left
 * to the caller.
 */
const useActivateIntegrationMode = () => {
    const { setIntegrationMode, isLoading } = useSetIntegrationMode();
    const { setBanner, emptyBanner } = useErrorBanner();
    const { data: checkoutMapSettings, mutate: mutateCheckoutMapSettings } = useCheckoutMapSettingsSWR();
    const { saveCheckoutMapSettings } = useCheckoutMapSettings();
    const { mapFromVo } = useCheckoutMapSettingsMapper();

    const [showCarrierServiceWarning, setShowCarrierServiceWarning] = useState(false);

    /** Clears the carrier service warning, e.g. when a different integration mode is picked */
    const resetCarrierServiceWarning = useCallback(() => setShowCarrierServiceWarning(false), []);

    const saveGoogleMapApiKey = useCallback(
        async (googleMapApiKey: string): Promise<boolean> => {
            try {
                await saveCheckoutMapSettings(
                    mapFromVo({ google_map_api_key: googleMapApiKey.trim() }, checkoutMapSettings || [])
                );
                await mutateCheckoutMapSettings();
                return true;
            } catch {
                setBanner({
                    titleKey: 'SETTINGS.CHECKOUT_MAP_INTEGRATION.SAVE_ERROR.TITLE',
                    descriptionKey: 'SETTINGS.CHECKOUT_MAP_INTEGRATION.SAVE_ERROR.CONTENT',
                    tone: 'warning',
                    withSupportActions: true,
                });
                return false;
            }
        },
        [saveCheckoutMapSettings, mapFromVo, checkoutMapSettings, mutateCheckoutMapSettings, setBanner]
    );

    const activateIntegrationMode = useCallback(
        async (mode: IntegrationModeSetting, { googleMapApiKey }: ActivateIntegrationModeOptions = {}) => {
            emptyBanner();
            // A new attempt starts without the previous attempt's warning
            setShowCarrierServiceWarning(false);
            shopify.loading(true);

            try {
                if (mode === IntegrationModeSetting.thank_you_page_map && googleMapApiKey?.trim()) {
                    const keySaved = await saveGoogleMapApiKey(googleMapApiKey);
                    if (!keySaved) return false;
                }

                const activated = await setIntegrationMode(mode, DELIVERY_CUSTOMIZATION_ACTIVE[mode], {
                    completeWizardStep: false,
                });

                // For the checkout list a failure is most likely the carrier service, so it's also flagged inline
                if (!activated && mode === IntegrationModeSetting.checkout) {
                    setShowCarrierServiceWarning(true);
                }

                return activated;
            } finally {
                shopify.loading(false);
            }
        },
        [emptyBanner, saveGoogleMapApiKey, setIntegrationMode]
    );

    return {
        activateIntegrationMode,
        showCarrierServiceWarning,
        resetCarrierServiceWarning,
        isLoading,
    };
};

export default useActivateIntegrationMode;
